import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import { StyledTextField } from 'components/inputs/styledTextField';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import styles from './styles';
import { createFinancialInfo } from 'store/actions/companyActions';
import PhoneInput from 'now-frontend-shared/components/inputs/PhoneInput';
import AmountInput from 'now-frontend-shared/components/inputs/AmountInput';
import { generalRules } from 'now-shared/validation/validation-rules';
import validateBidAllowance from 'now-frontend-shared/validation/validateBidAllowance';
import { navigateToNextPage, navigateToPreviousPage } from 'constants/registrationFlow';

function FinancingInformationView({ classes, authUser }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [financial, setFinancial] = useState({
    bankName: '',
    bankerName: '',
    bankerPhoneNumber: '',
    requestedBidAllowance: '',
    notes: '',
  });
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [touchedPhone, setTouchedPhone] = useState(false);
  const [touchedAllowance, setTouchedAllowance] = useState(false);
  const [terms, setTerms] = useState(false);

  React.useEffect(() => {
    setFinancial({
      bankName: authUser.company?.bankInformation?.bankName,
      bankerName: authUser.company?.bankInformation?.bankerName,
      bankerEmail: authUser.company?.bankInformation?.bankerEmail,
      bankerPhoneNumber: authUser.company?.bankInformation?.bankerPhoneNumber,
      requestedBidAllowance: authUser.company?.bankInformation?.requestedBidAllowance,
      notes: authUser.company?.bankInformation?.notes,
    });
  }, [authUser]);

  const financialFields = [
    { label: 'Name of Bank or Financial Institution', value: 'bankName' },
    { label: 'Name of Banker or Private Equity Contact', value: 'bankerName' },
  ];
  const financialFields2 = [{ label: 'Notes/Instructions', value: 'notes' }];
  const createFields = (list, func, state, autoFocus = false) => (
    <>
      {list.map(({ label, value }, i) => (
        <StyledTextField
          value={state[value]}
          key={label}
          multiline={value === 'notes'}
          rows={4}
          variant="outlined"
          onChange={e => {
            func({ ...state, [value]: e.target.value });
          }}
          id="standard-full-width"
          label={label}
          placeholder={label}
          fullWidth
          margin="normal"
          autoFocus={autoFocus && i === 0}
        />
      ))}
    </>
  );

  const { notes, ...restFinancial } = financial;
  const presentFieldsValidationError = Object.values(restFinancial).some(v => !v);
  const emailValidationError = generalRules.required(financial.bankerEmail) || generalRules.email(financial.bankerEmail);
  const phoneValidationError = generalRules.phoneNumber(financial.bankerPhoneNumber);
  const bidAllowanceValidationError = validateBidAllowance(financial.requestedBidAllowance);
  const areFinancialFieldsValid = !presentFieldsValidationError
    && !emailValidationError
    && !phoneValidationError
    && !bidAllowanceValidationError;

  return (
    <div className={classes.root}>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        direction="column"
        style={{ margin: 'auto', maxWidth: '642px' }}
      >
        <Typography variant="h1" component="div" className={classes.title}>
          Financing Information
        </Typography>
        {createFields(financialFields, setFinancial, financial, true)}
        <StyledTextField
          type="email"
          value={financial.bankerEmail}
          onChange={e => {
            if (!touchedEmail) {
              setTouchedEmail(true);
            }
            setFinancial({ ...financial, bankerEmail: e.target.value });
          }}
          onBlur={() => {
            if (!touchedEmail) {
              setTouchedEmail(true);
            }
          }}
          error={touchedEmail && !!emailValidationError}
          helperText={touchedEmail && emailValidationError}
          label="Email of Banker or Private Equity Contact"
          placeholder="Email of Banker or Private Equity Contact"
          variant="outlined"
          margin="normal"
          fullWidth
        />
        <PhoneInput
          classes={{ maskStyles: classes.phoneInput }}
          meta={{
            touched: touchedPhone,
            error: generalRules.phoneNumber(financial.bankerPhoneNumber),
          }}
          placeholder="Telephone Number for Banker or Private Equity Contact"
          input={{
            value: financial.bankerPhoneNumber,
            name: 'bankerPhoneNumber',
            onChange: e => {
              if (e.type === 'focus') {
                return;
              }
              if (!touchedPhone) {
                setTouchedPhone(true);
              }
              setFinancial({ ...financial, bankerPhoneNumber: e.target.value });
            },
            onBlur: () => {
              if (!touchedPhone) {
                setTouchedPhone(true);
              }
            },
          }}
        />
        <AmountInput
          classes={{ fieldStyles: classes.amountInput }}
          placeholder="Requested Bid Allowance"
          meta={{
            touched: touchedAllowance,
            error: bidAllowanceValidationError,
          }}
          input={{
            value: financial.requestedBidAllowance,
            onChange: e => {
              if (!touchedAllowance) {
                setTouchedAllowance(true);
              }
              setFinancial({
                ...financial,
                requestedBidAllowance: e.target.value,
              });
            },
            onBlur: () => {
              if (!touchedAllowance) {
                setTouchedAllowance(true);
              }
            },
            name: 'requestedBidAllowance',
          }}
        />
        {createFields(financialFields2, setFinancial, financial, false)}
        <FormControlLabel
          className={classes.label}
          control={(
            <Checkbox
              color="default"
              checked={terms || authUser.company?.bankInformation?.bankName}
              onChange={() => setTerms(!terms)}
            />
          )}
          label="By checking this box, I agree to contact my entity's financial provider listed above, in order to allow representatives from NOW to speak to my financial provider to verify my entity's access to funds"
        />
        <div className={classes.buttonContainer}>
          <Button
            variant="text"
            className={classes.backButton}
            onClick={() => history.push(navigateToPreviousPage(history.location.pathname))}
          >
            BACK
          </Button>
          <Button
            className={classes.button}
            variant="contained"
            disabled={!areFinancialFieldsValid || (!terms && !authUser.company?.bankInformation?.bankName)}
            onClick={async () => {
              if (!authUser.company?.bankInformation?.bankName) {
                try {
                  await new Promise((resolve, reject) => dispatch(createFinancialInfo({
                    financial,
                    resolve,
                    reject,
                  })));
                } catch (e) {
                  console.log(e);
                }
              }
              history.push(navigateToNextPage(history.location.pathname));
            }}
          >
            NEXT
          </Button>
        </div>
      </Grid>
    </div>
  );
}

FinancingInformationView.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  authUser: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default compose(
  connect(({ auth }) => ({
    authUser: auth.user,
  })),
  withStyles(styles),
)(FinancingInformationView);
